/**
 * CheckInForm Component
 * Form for submitting weekly check-ins
 */

import React, { useState, useMemo } from 'react';
import { AlertCircle, CheckCircle, AlertTriangle, Star, Send } from 'lucide-react';
import { Card, Button, LoadingSpinner } from '../common';
import { useCreateCheckIn, useCheckIns } from '../../hooks';
import { useAuth } from '../../context/AuthContext';
import { useCanUseAI } from '../../hooks/useAIConfig';
import type { OKR, CreateCheckInRequest, CheckInStatus } from '../../types/okr';
import { canSummarizeCheckInNotes } from '@okrhub/shared';
import { aiCoachService } from '../../services/aiCoach.service';
import { toast } from 'react-hot-toast';
import { CheckInKRCard } from './CheckInKRCard';
import { computeKRStaleLabel } from '../../utils/checkInStaleness';

interface CheckInFormProps {
  okr: OKR;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const statusOptions: Array<{
  value: CheckInStatus;
  label: string;
  icon: typeof CheckCircle;
  color: string;
  bgColor: string;
  borderColor: string;
}> = [
  {
    value: 'on_track',
    label: 'On Track',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-300'
  },
  {
    value: 'at_risk',
    label: 'At Risk',
    icon: AlertTriangle,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-300'
  },
  {
    value: 'off_track',
    label: 'Off Track',
    icon: AlertCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-300'
  }
];

export const CheckInForm: React.FC<CheckInFormProps> = ({
  okr,
  onSuccess,
  onCancel
}) => {
  const createCheckInMutation = useCreateCheckIn();
  const { user } = useAuth();
  const { isEligible, aiEnabled } = useCanUseAI();
  const aiKnownDisabled = !isEligible || !aiEnabled;

  // For team OKRs: if user is not the OKR owner, only show KRs assigned to them
  const isOKROwner = user?.id === okr.ownerId;
  const isTeamOKR = okr.type === 'team';
  const visibleKeyResults = useMemo(() => {
    if (!isTeamOKR || isOKROwner) {
      return okr.keyResults; // OKR owner sees all KRs
    }
    // KR owner sees only their assigned KRs
    return okr.keyResults.filter(kr => kr.ownerId === user?.id);
  }, [okr.keyResults, isTeamOKR, isOKROwner, user?.id]);

  // Per-KR staleness: "no notes · N weeks" from recent check-in history, so a KR
  // that has gone quiet is flagged on its collapsed card. Frontend-only.
  // Uses an explicit larger page than the default 10 so the staleness reference
  // (last-noted / oldest-available check-in) is accurate across a full cycle of
  // weekly check-ins. This intentionally uses a different query key than
  // CheckInHistory's default `useCheckIns(okrId)`, so it is a separate fetch.
  const STALENESS_HISTORY_LIMIT = 50;
  const { data: checkInData } = useCheckIns(okr.id, { limit: STALENESS_HISTORY_LIMIT });
  const staleLabels = useMemo(
    () =>
      Object.fromEntries(
        visibleKeyResults.map((kr) => [kr.id, computeKRStaleLabel(checkInData?.checkIns, kr.id)])
      ),
    [checkInData, visibleKeyResults]
  );

  const [formData, setFormData] = useState<CreateCheckInRequest>({
    status: 'on_track',
    confidenceLevel: 3,
    keyResultUpdates: visibleKeyResults.map(kr => ({
      keyResultId: kr.id,
      newValue: kr.currentValue
    })),
    overallSummary: '',
    keyResultNotes: visibleKeyResults.map((kr) => ({
      keyResultId: kr.id,
      accomplishments: '',
      challenges: '',
      nextSteps: '',
    })),
  });

  const handleStatusChange = (status: CheckInStatus) => {
    setFormData(prev => ({ ...prev, status }));
  };

  const handleConfidenceChange = (level: number) => {
    setFormData(prev => ({ ...prev, confidenceLevel: level }));
  };

  const handleKRValueChange = (keyResultId: string, newValue: number) => {
    setFormData(prev => ({
      ...prev,
      keyResultUpdates: prev.keyResultUpdates?.map(kr =>
        kr.keyResultId === keyResultId ? { ...kr, newValue } : kr
      )
    }));
  };

  const [expandedKRs, setExpandedKRs] = useState<Record<string, boolean>>({});

  const toggleKR = (keyResultId: string) =>
    setExpandedKRs((prev) => ({ ...prev, [keyResultId]: !prev[keyResultId] }));

  const handleNoteChange = (
    keyResultId: string,
    field: 'accomplishments' | 'challenges' | 'nextSteps',
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      keyResultNotes: prev.keyResultNotes?.map((n) =>
        n.keyResultId === keyResultId ? { ...n, [field]: value } : n
      ),
    }));
  };

  const expandAll = () =>
    setExpandedKRs(Object.fromEntries(visibleKeyResults.map((kr) => [kr.id, true])));

  const [summarizing, setSummarizing] = useState(false);

  // AI summarize precondition (shared with the backend guard): at least one KR
  // has BOTH Accomplishments and Challenges filled.
  const canSummarize = useMemo(
    () => canSummarizeCheckInNotes(formData.keyResultNotes),
    [formData.keyResultNotes]
  );

  const handleSummarize = async () => {
    if (!canSummarize) return;
    setSummarizing(true);
    try {
      const { summary } = await aiCoachService.summarizeCheckIn({
        okrId: okr.id,
        notes: (formData.keyResultNotes ?? []).map((n) => ({
          keyResultId: n.keyResultId,
          accomplishments: n.accomplishments,
          challenges: n.challenges,
          nextSteps: n.nextSteps,
        })),
      });
      if (summary) setFormData((prev) => ({ ...prev, overallSummary: summary }));
      else toast('Not enough notes yet to summarize.');
    } catch (e: any) {
      toast.error(e?.message || 'Could not generate summary');
    } finally {
      setSummarizing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await createCheckInMutation.mutateAsync({
        okrId: okr.id,
        data: formData
      });
      onSuccess?.();
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  return (
    <Card className="p-6">
      <form onSubmit={handleSubmit}>
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Weekly Check-in</h3>

        {/* Status Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status *
          </label>
          <div className="grid grid-cols-3 gap-3">
            {statusOptions.map(({ value, label, icon: Icon, color, bgColor, borderColor }) => (
              <button
                key={value}
                type="button"
                onClick={() => handleStatusChange(value)}
                className={`flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-all ${
                  formData.status === value
                    ? `${bgColor} ${borderColor} ${color}`
                    : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium text-sm">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Confidence Level */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Confidence Level
          </label>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => handleConfidenceChange(level)}
                className={`p-1.5 rounded-lg transition-all hover:bg-yellow-50 ${
                  formData.confidenceLevel && formData.confidenceLevel >= level
                    ? 'text-yellow-500'
                    : 'text-gray-300'
                }`}
              >
                <Star className={`w-6 h-6 ${
                  formData.confidenceLevel && formData.confidenceLevel >= level
                    ? 'fill-yellow-500'
                    : ''
                }`} />
              </button>
            ))}
            <span className="ml-3 text-sm text-gray-500">
              {formData.confidenceLevel}/5
            </span>
          </div>
        </div>

        {/* Per-KR progress + notes */}
        {visibleKeyResults.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-bold text-gray-900">
                {isTeamOKR && !isOKROwner ? 'Your Key Results — progress & notes' : 'Key Results — progress & notes'}
              </h3>
              <button type="button" onClick={expandAll} className="text-xs text-primary-600">
                Expand all
              </button>
            </div>
            {visibleKeyResults.map((kr) => {
              const note = formData.keyResultNotes?.find((n) => n.keyResultId === kr.id);
              const upd = formData.keyResultUpdates?.find((u) => u.keyResultId === kr.id);
              return (
                <CheckInKRCard
                  key={kr.id}
                  okrId={okr.id}
                  kr={kr}
                  value={upd?.newValue ?? kr.currentValue}
                  notes={{
                    accomplishments: note?.accomplishments ?? '',
                    challenges: note?.challenges ?? '',
                    nextSteps: note?.nextSteps ?? '',
                  }}
                  expanded={!!expandedKRs[kr.id]}
                  staleLabel={staleLabels[kr.id]}
                  onToggle={() => toggleKR(kr.id)}
                  onValueChange={(v) => handleKRValueChange(kr.id, v)}
                  onNoteChange={(field, value) => handleNoteChange(kr.id, field, value)}
                />
              );
            })}
          </div>
        )}

        {/* Optional overall summary */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Overall summary <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            {!aiKnownDisabled && (
              <button
                type="button"
                onClick={handleSummarize}
                disabled={summarizing || !canSummarize}
                title={
                  canSummarize
                    ? 'Draft an overall summary from your per-KR notes'
                    : 'Add Accomplishments and Challenges to at least one Key Result first'
                }
                className="text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-lg px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {summarizing ? 'Summarizing…' : '✨ Summarize with AI'}
              </button>
            )}
          </div>
          <textarea
            value={formData.overallSummary || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, overallSummary: e.target.value }))}
            placeholder="Write an overall summary, or let AI draft one from your per-KR notes…"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          {onCancel && (
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={createCheckInMutation.isPending}
          >
            {createCheckInMutation.isPending ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Check-in
              </>
            )}
          </Button>
        </div>
      </form>
    </Card>
  );
};
